import {
  MercadoPagoInstance,
  PaymentBrickController,
} from 'src/features/paymentMethods/presentation/paymentMethods.types';

declare global {
  interface Window {
    paymentBrickController?: PaymentBrickController;
    MercadoPago: new (
      publicKey: string,
      params?: { locale: string }
    ) => MercadoPagoInstance;
  }
}

export enum AppSubdomains {
  Homebar = 'homebar',
  Terea = 'pmi',
  Pari = 'tienda',
}

export interface Category {
  name: string;
  slug_name: string;
  image?: string | null;
  thumbnail_image?: string | null;
}

type ProductImage = {
  is_main?: boolean;
  image: string;
};

export type ProductInfo = {
  pk: number;
  name: string;
  slug_name: string;
  description: string;
  main_photo?: string;
  deep_link?: string;
  images?: ProductImage[];
  _categories?: string[];
  _tags?: string[];
};

export type Product = {
  product: ProductInfo;
  price: string;
  discount_price?: string | null;
  quantity: number;
  discount_percentage?: string;
  sale_price?: string;
  b2b_price?: string;
  dry_law?: boolean;
};

export interface CategoryWithProducts extends Category {
  products: Product[];
}

export interface AddressGeolocation {
  type?: string;
  coordinates: [number, number];
}

export interface AddressDetails {
  street_name: string;
  street_number: string;
  extra_info: null | string;
  geolocation: AddressGeolocation;
  city: null | string;
  state: null | string | { name: string; country: string };
  zip_code: string | null;
  neighborhood: string | null;
  formatted_address: string;
  interior_number: string | null;
  name: string;
}

export interface Address {
  pk: number;
  is_main: boolean;
  name: string;
  address: AddressDetails;
  store_data: { slug_name: string; name: string } | null;
}

export interface UserProfile {
  birth_date: string;
  document_type: DocumentTypes | null;
  document_number: string | null;
}

export interface UserDetails {
  pk: number;
  uuid: string;
  email: string;
  email_verified: boolean;
  first_name: string;
  last_name: string;
  phone_number: string;
  phone_verified: boolean;
  profile: UserProfile;
  main_address: Address | null;
  type: UserType;
  active_cart: number | null;
  is_completed: boolean;
  id_verified: boolean;
}

export enum UserType {
  B2b = 'b2b',
  B2c = 'client',
}

export type OrderStatusName =
  | 'Pendiente'
  | 'Preparando'
  | 'En entrega'
  | 'Entregado'
  | 'Cancelado';

export enum PaymentStatusDetail {
  Accredited = 'accredited',
  PendingChallenge = 'pending_challenge',
  Rejected3DSChallenge = 'cc_rejected_3ds_challenge',
  Rejected3DSMandatory = 'cc_rejected_3ds_mandatory',
  Expired = 'expired',
  PartiallyRefunded = 'partially_refunded',
  PendingCapture = 'pending_capture',
  OfflineProcess = 'offline_process',
  PendingContingency = 'pending_contingency',
  PendingReviewManual = 'pending_review_manual',
  PendingWaitingTransfer = 'pending_waiting_transfer',
  PendingWaitingPayment = 'pending_waiting_payment',
  BankError = 'bank_error',
  CCRejectedBadFilledCardNumber = 'cc_rejected_bad_filled_card_number',
  CCRejectedBadFilledDate = 'cc_rejected_bad_filled_date',
  CCRejectedBadFilledOther = 'cc_rejected_bad_filled_other',
  CCRejectedBadFilledSecurityCode = 'cc_rejected_bad_filled_security_code',
  CCRejectedBlacklist = 'cc_rejected_blacklist',
  CCRejectedCallForAuthorize = 'cc_rejected_call_for_authorize',
  CCRejectedCardDisabled = 'cc_rejected_card_disabled',
  CCRejectedDuplicatedPayment = 'cc_rejected_duplicated_payment',
  CCRejectedHighRisk = 'cc_rejected_high_risk',
  CCRejectedInsufficientAmount = 'cc_rejected_insufficient_amount',
  CCRejectedInvalidInstallments = 'cc_rejected_invalid_installments',
  CCRejectedMaxAttempts = 'cc_rejected_max_attempts',
  CCRejectedOtherReason = 'cc_rejected_other_reason',
  CCRejectedTimeOut = 'cc_rejected_time_out',
  CCAmountRateLimitExceeded = 'cc_amount_rate_limit_exceeded',
  RejectedHighRisk = 'rejected_high_risk',
  RejectedInsufficientData = 'rejected_insufficient_data',
  RejectedByBank = 'rejected_by_bank',
  RejectedByRegulations = 'rejected_by_regulations',
  RejectedByBizRule = 'rejected_by_biz_rule',
}

export enum MPPaymentDataStatus {
  Approved = 'approved',
  Rejected = 'rejected',
  Pending = 'pending',
  Cancelled = 'cancelled',
}

export enum ClipPaymentDataStatus {
  CheckoutCreated = 'CHECKOUT_CREATED',
  CheckoutPending = 'CHECKOUT_PENDING',
  CheckoutCancelled = 'CHECKOUT_CANCELLED',
  CheckoutExpired = 'CHECKOUT_EXPIRED',
  CheckoutCompleted = 'CHECKOUT_COMPLETED',
}

export type PaymentDataStatus =
  | MPPaymentDataStatus
  | 'completed'
  | ClipPaymentDataStatus
  | 'charge_pending';

export enum OrderStatusDetailValues {
  PendingPayment = 'pending_payment',
  PaymentApproved = 'payment_approved',
  PaymentRejected = 'payment_rejected',
}

export type OrderStatus = {
  status_hr: OrderStatusName;
  created: string;
};

export interface OrderBase {
  id: number;
  courier: null;
  status_hr: OrderStatusName;
  estimated_duration: number;
  created: string;
}

export type OrderPaymentData = {
  id: string;
  payment_method_id: string;
  external_resource_url: string | null;
  transaction_details?: {
    external_resource_url: string;
    payment_method_reference_id: string;
    total_paid_amount: number;
  };
  receipt: {
    file_url: string;
  } | null;
  discount_percentage: 4 | null;
  total_with_discount: number | null;
  reference_code?: string;
  three_ds_info: ThreeDSInfo | null;
  status_detail: PaymentStatusDetail | null;
  status: PaymentDataStatus;
  init_point: string | null; // For mercado pago wallet payment method
  payment_request_url: string | null; // For Clip payment method
};

export type ThreeDSInfo = { external_resource_url: string; creq: string };

export interface OrderInfo extends OrderBase {
  address: string;
  statuses: OrderStatus[];
  products: Product[];
  total: string;
  store: string;
  coupon: string | null;
  delivery_price: string;
  payment_method: PaymentMethodName;
  sub_total: string;
  extra_data?: {
    delivery_info:
      | {
          method: DeliveryMethod;
          date: string | null;
          time: string | null;
          pincode?: string;
        }
      | undefined;
    mercado_pago_preference: {
      init_point: string | null;
      preference_id: string | null;
    } | null;
    clip_redirect_preference: {
      init_point: string | null;
      preference_id: string | null;
    } | null;
  };
  payment_data?: OrderPaymentData;
  observations?: string;
  coupon_info?: OrderCouponInfo | null;
  status_detail: OrderStatusDetailValues | null;
  wallet: number | null;
}

export type OrderCouponInfo = {
  code: string;
  total_with_coupon: number;
};

export interface Banner {
  image: string;
  deep_link: string | null;
  ordering: number;
  name: string;
  slug_name: string;
}

export type FormErrors<Values> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [K in keyof Values]?: Values[K] extends any[]
    ? Values[K][number] extends object
      ? FormErrors<Values[K][number]>[] | string | string[]
      : string | string[]
    : Values[K] extends object
      ? FormErrors<Values[K]>
      : string;
};

export type FormTouched<Values> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [K in keyof Values]?: Values[K] extends any[]
    ? Values[K][number] extends object
      ? FormTouched<Values[K][number]>[]
      : boolean
    : Values[K] extends object
      ? FormTouched<Values[K]>
      : boolean;
};
export type ErrorResponsePayload = {
  [x: string]: string[];
};

export type StoreData = {
  slug_name: string;
  name: string;
};

export type Session = {
  accessToken: string;
  user: UserDetails;
};

export type CartProduct = {
  product: ProductInfo;
  quantity: number;
  price: string;
  available_quantity: number;
};

export type Cart = {
  pk: number;
  store: string;
  user: string;
  is_active: boolean;
  is_b2b: boolean;
  products: CartProduct[];
  sub_total: string;
  prices:
    | {
        mxn_to_free: number;
        delivery_price: number;
      }
    | undefined;
  get_total: number;
  total_quantity: number;
  coupon_details?: CartCoupon | null;
};

export type CouponCondition = {
  message: string;
  success?: boolean;
};

export type CouponDetails = {
  title: string | null;
  is_strict: boolean;
  conditions: CouponCondition[];
  code: string;
  terms_and_conditions: string | null;
};

export type CartCoupon = CouponDetails & {
  total_with_coupon: number | null;
};

export enum CouponType {
  Gift = 'GIFT',
  Discount = 'DISCOUNT',
}

export type PaymentMethod = {
  slug_name: PaymentMethodSlug;
  name: string;
  pk: number;
  kalto_slug: string;
};

export enum PaymentMethodSlug {
  OpenPay = 'openpay',
  PariCredit = 'pari-credito',
  BankTransfer = 'transferencia',
  Card = 'mercado-pago',
  PariTransfer = 'transferencia-pari',
  MercadoPago = 'mercado-pago-wallet',
  Clip = 'clip-redirect',
}

export enum PaymentMethodName {
  OpenPay = 'openpay',
  PariCredit = 'Pari crédito',
  BankTransfer = 'Transferencia',
  Card = 'Tarjeta Crédito / Débito',
  PariTransfer = 'Transferencia Pari',
  MercadoPago = 'Mercado Pago Wallet',
  Clip = 'Clip Redirect',
}

export type DeliveryMethod = 'scheduled' | 'express';

export type DeliveryMethodInfo =
  | {
      method: 'scheduled';
      date: Date | null;
      time: string | null;
    }
  | {
      method: 'express';
      date: null;
      time: null;
    };

export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type ISODateString = string;

export type PariBankAccountDetails = {
  bank: string;
  beneficiary: string;
  clabe: string;
};

export type FileFormats = '.jpg' | '.jpeg' | '.png' | '.pdf';

export type FileUploadRules = {
  formats: FileFormats[];
  maxSizeBytes: number;
  maxFiles: number;
};

export enum AddressName {
  Home = 'Casa',
  Office = 'Oficina',
  Other = 'Otro',
}

export enum ThreeDSecureModes {
  Optional = 'optional',
  Mandatory = 'mandatory',
  NotSupported = 'not_supported',
}

export type SocialAuthProviders = {
  google: boolean;
  facebook: boolean;
  apple: boolean;
};

export enum InAppNotificationType {
  FullScreen = 'fullscreen',
  Modal = 'modal',
  SnackBar = 'snack_bar',
}

export enum NotificationActionType {
  Url = 'url',
}

export enum NotificationActionContentType {
  Image = 'image',
  Video = 'video',
}

interface InAppNotificationBase {
  pk: number;
  title: string | null;
  message: string | null;
  type: InAppNotificationType;
  action_content_type: NotificationActionContentType;
  action_content: string;
  is_persistent: boolean | null;
}

interface UrlNotification extends InAppNotificationBase {
  action_type: NotificationActionType.Url;
  action_data: {
    url: string;
  };
}

export type InAppNotification = UrlNotification;

export type PariWallet = {
  uuid: string;
  amount_available: string;
};

export enum WalletTransactionType {
  Gift = 'regalo',
  Purchase = 'compra',
  Refund = 'reembolso',
  Expired = 'expirado',
}

export interface PariWalletTransaction {
  id: number;
  type: {
    name: string;
    slug_name: WalletTransactionType;
  };
  amount: string;
  wallet_pk: number;
  created: string;
  modified: string;
  wallet: {
    uuid: string;
    amount_available: string;
  };
  description: string | null;
  expiration_date: string | null;
}

export enum LocalStorageKeys {
  Session = 'session',
  TermsAcceptance = 'termsAcceptance',
  SeenNotifications = 'seen_notifications',
  WalletReadToken = 'walletReadToken',
  SearchHistory = 'searchHistory',
  AmplitudeSessionId = 'amplitudeSessionId',
  AnonymousWantedProduct = 'anonymousWantedProduct',
  CartExpirationTime = 'terea-layout-timer',
  VerificationToken = 'terea-jwt',
}

export type LocalStorageValues = {
  [LocalStorageKeys.Session]: Session | undefined;
  [LocalStorageKeys.TermsAcceptance]: boolean | undefined;
  [LocalStorageKeys.SeenNotifications]: number[] | undefined;
  [LocalStorageKeys.WalletReadToken]: string | undefined;
  [LocalStorageKeys.SearchHistory]: string[] | undefined;
  [LocalStorageKeys.AmplitudeSessionId]: number | undefined;
  [LocalStorageKeys.AnonymousWantedProduct]: ProductInfo | null;
  [LocalStorageKeys.CartExpirationTime]: number | null;
  [LocalStorageKeys.VerificationToken]: string | null;
};

export enum DocumentTypes {
  INE = 'ine',
  PASSPORT = 'passport',
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export enum VerificationChannelLabels {
  email = 'Correo electrónico',
  sms = 'SMS',
  whatsapp = 'WhatsApp',
}

export enum VerificationChannels {
  email = 'email',
  sms = 'sms',
  whatsapp = 'whatsapp',
}

export interface DecodedJWTUser {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  birth_date?: string;
  t_and_c: boolean;
  exp?: number;
  address?: {
    street_name: string;
    street_number: string;
    latitude: number;
    longitude: number;
    city: string;
    state: string;
    zip_code: string;
    interior_number?: string;
    neighborhood: string;
    formatted_address: string;
  };
  store?: string;
}

export type OrderReview = {
  order_pk: number;
  store: string;
  created_at: string;
  products: Product[];
};
